import type { ReactNode } from 'react';
import { SemanticIcon, type SemanticIconName } from './SemanticIcon';

export type SemanticFeedbackKind = 'notice' | 'success' | 'warning' | 'error' | 'progress';

const feedbackIcons: Record<SemanticFeedbackKind, SemanticIconName> = {
  notice: 'sparkle',
  success: 'success',
  warning: 'offline',
  error: 'error',
  progress: 'refresh',
};

export interface SemanticFeedbackProps {
  kind: SemanticFeedbackKind;
  title: string;
  children?: ReactNode;
  action?: ReactNode;
  progress?: number;
  className?: string;
}

export function SemanticFeedback({ kind, title, children, action, progress, className = '' }: SemanticFeedbackProps) {
  const boundedProgress = typeof progress === 'number' ? Math.min(100, Math.max(0, progress)) : undefined;
  return (
    <div className={`opp-semantic-feedback opp-semantic-feedback--${kind} ${className}`.trim()} role={kind === 'error' ? 'alert' : 'status'} aria-live="polite">
      <span className="opp-semantic-feedback__icon" aria-hidden="true">
        <SemanticIcon name={feedbackIcons[kind]} size={18} className={kind === 'progress' ? 'opp-semantic-state__spin' : undefined} />
      </span>
      <div className="opp-semantic-feedback__body">
        <strong>{title}</strong>
        {children && <span>{children}</span>}
        {boundedProgress !== undefined && (
          <span className="opp-semantic-feedback__progress" aria-label={`${boundedProgress}%`}>
            <span style={{ width: `${boundedProgress}%` }} />
          </span>
        )}
      </div>
      {action && <div className="opp-semantic-feedback__action">{action}</div>}
    </div>
  );
}

export default SemanticFeedback;
